import React, { useImperativeHandle, forwardRef } from "react";
import PropTypes from "prop-types";

import { AdContainerOuterWrapper } from "../components/AdContainerOuterWrapper";
import { TopControls, topControlsProps } from "../components/TopControls";
import BottomControls from "../components/BottomControls";
import useIrisEventListeners from "../hooks/useIrisEventListeners";
import { useVideoAnalyticsContext } from "../../contexts/VideoAnalyticsContext";
import {
  useAdControlsContext,
  AdActionType
} from "../contexts/AdControlsContext";
import { useAdsElementsRefContext } from "../../contexts/AdsElementsRefContext";
import { useOrcaCommonContext } from "../../contexts/OrcaCommonContext";
import { useOrcaAdsContext } from "../../contexts/OrcaAdsContext";

// The AdContainer contains a rendered Zeus Iris advertisement. It includes controls for the ad and event listeners to record ad analytics
const AdContainer = forwardRef(
  (
    {
      setPromoState,
      transitionToVideo,
      debug,
      setRenderControls,
      updateDisplay = () => {},
      isAdsInitialized
    },
    ref
  ) => {
    const { play, pause, togglePlay, toggleAdMute, skip, dispatchAdAction } =
      useAdControlsContext();
    const { setAvAdMeta, setAdAttribute } = useVideoAnalyticsContext();
    const { adWrapperRef } = useAdsElementsRefContext();
    const {
      playerState,
      isFullscreen,
      closeStickyPlayer,
      pause: pauseVideo
    } = useOrcaCommonContext();
    const { adState, setAdState, adSettings } = useOrcaAdsContext();

    useImperativeHandle(ref, () => ({
      playAd() {
        play();
      },

      pauseAd() {
        pause();
      },

      displayControls() {
        dispatchAdAction({
          type: AdActionType.SET_SHOW_CONTROLS,
          showControls: true
        });
      },

      toggleAdMute() {
        toggleAdMute();
      },

      skipAd() {
        skip();
      }
    }));

    const advertiserUrl =
      adWrapperRef.current?.irisPlayer?.getCurrentAd()?.data?.clickThroughUrl;

    useIrisEventListeners({
      adSettings,
      adState,
      debug,
      transitionToVideo,
      setAdAttribute,
      setAdState,
      setAvAdMeta,
      setPromoState,
      setRenderControls,
      playerState,
      updateDisplay,
      isAdsInitialized,
      pauseVideo
    });

    return (
      <AdContainerOuterWrapper
        advertiserUrl={advertiserUrl}
        togglePlay={togglePlay}
      >
        <TopControls
          advertiserUrl={advertiserUrl}
          closeStickyPlayer={closeStickyPlayer}
          isFullscreen={isFullscreen}
        />
        <BottomControls
          skip={skip}
          toggleAdPlay={togglePlay}
          toggleMute={toggleAdMute}
        />
      </AdContainerOuterWrapper>
    );
  }
);

AdContainer.displayName = "AdContainer";

export default AdContainer;

AdContainer.propTypes = {
  toggleMute: PropTypes.func,
  playerState: PropTypes.object,
  setPromoState: PropTypes.func,
  adSettings: PropTypes.object,
  transitionToVideo: PropTypes.func,
  setAdState: PropTypes.func,
  debug: PropTypes.bool,
  setRenderControls: PropTypes.func,
  adState: PropTypes.string,
  updateDisplay: PropTypes.func,
  ...topControlsProps
};
